import { Entity } from "./entity";
import { Exclude, Expose, plainToClass } from "class-transformer";
import * as t from "io-ts";
import { v4 as uuid } from "uuid";

/** Validator for unit of measures */
export const UnitOfMeasureValidator = t.intersection([
  t.type({
    id: t.string,
    type: t.string,
    name: t.string,
    abbr: t.string,
  }),
  t.partial({
    unitOfMeasureOffset: t.number,
  }),
]);

/** Validator for an JSON array of unit of measures */
export const UnitOfMeasureArrayValidator = t.array(UnitOfMeasureValidator);

export type UnitOfMeasure = t.TypeOf<typeof UnitOfMeasureValidator>;
export type UnitOfMeasureArray = t.TypeOf<typeof UnitOfMeasureArrayValidator>;

export interface UnitOfMeasureType {
  code: string;
  displayValue: string;
}

const volume: UnitOfMeasureType = {
  code: 'Volumn',
  displayValue: 'Volume',
};

const linear: UnitOfMeasureType = {
  code: 'Linear',
  displayValue: 'Linear',
};

const unit: UnitOfMeasureType = {
  code: 'Unit',
  displayValue: 'Unit',
};

const temperature: UnitOfMeasureType = {
  code: 'Temperature',
  displayValue: 'Temperature',
};

const weight: UnitOfMeasureType = {
  code: 'Weight',
  displayValue: 'Weight',
};

const volumeSolid: UnitOfMeasureType = {
  code: 'VolumeSolid',
  displayValue: 'Volume Solid',
};

/**
 * Types for {@link UnitOfMeasure}.
 */
export const unitOfMeasureTypes = {
  volume,
  linear,
  unit,
  temperature,
  weight,
  volumeSolid,
};

/**
 * Return UnitOfMeasureType by code value.
 *
 * @param code - Code value
 * @returns {UnitOfMeasureType} The matching UnitOfMeasureType
 */
export const toUnitOfMeasureType = (code: string): UnitOfMeasureType => {
  for (const type of Object.values(unitOfMeasureTypes)) {
    if (type.code === code) {
      return type;
    }
  }
  return { code, displayValue: code };
};

/**
 * A unit of measure.
 */
@Exclude()
export class UnitOfMeasureEntity implements Entity {
  @Expose() id!: string;
  @Expose({ name: 'type' }) private _type!: string;
  @Expose() name!: string;
  @Expose() abbr!: string;
  @Expose() unitOfMeasureOffset?: number;

  readonly entityType = 'UNITOFMEASURE';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for UnitOfMeasureEntity. Recommend using the {@link create} method instead.
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a UnitOfMeasureEntity.
   *
   * @param name The name of the unit of measure.
   * @param abbr The abbreviated name of the unit of measure.
   * @param type The unit of measure type.
   * @param id (optional) The primary key of the water type entity.
   */
  static create(
    name: string,
    abbr: string,
    type: UnitOfMeasureType,
    id: string | undefined = uuid()
  ): UnitOfMeasureEntity {
    const entity = new UnitOfMeasureEntity();
    entity.id = id;
    entity.name = name;
    entity.abbr = abbr;
    entity._type = type.code;
    return entity;
  }

  /**
   * Create a UnitOfMeasureEntity from a UnitOfMeasure type value.
   *
   * @param unitOfMeasure The unit of measure type value.
   */
  static from(unitOfMeasure: UnitOfMeasure): UnitOfMeasureEntity {
    const entity: UnitOfMeasureEntity = plainToClass(
      UnitOfMeasureEntity,
      unitOfMeasure,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  get type(): UnitOfMeasureType {
    /* istanbul ignore next */
    return toUnitOfMeasureType(this._type);
  }

  set type(type: UnitOfMeasureType) {
    /* istanbul ignore next */
    this._type = type.code;
  }
}
